// Calendar-specific score color scale (matches the spec's 5 bands).
// Values are full-opacity HSL/HEX in the warm wellness palette, then alpha
// tuned per band so the empty cells stay subtle while filled cells pop.
export function calendarColor(score) {
  if (score == null) return "rgba(196, 178, 144, 0.18)"; // empty / beige
  const s = Number(score);
  if (s < 2) return "rgba(224, 122, 95, 0.78)";          // coral
  if (s < 4) return "rgba(232, 154, 86, 0.80)";          // terracotta
  if (s < 6) return "rgba(233, 196, 106, 0.82)";         // amber
  if (s < 8) return "rgba(141, 187, 142, 0.85)";         // sage suave
  return "rgba(42, 157, 143, 0.95)";                      // sage profundo
}

export function calendarBandLabel(score) {
  if (score == null) return "Sin registro";
  if (score < 2) return "Muy bajo";
  if (score < 4) return "A mejorar";
  if (score < 6) return "Aceptable";
  if (score < 8) return "Bueno";
  return "Excelente";
}

const MONTHS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const MONTHS_FULL = ["enero","febrero","marzo","abril","mayo","junio","julio","agosto","septiembre","octubre","noviembre","diciembre"];
const DAYS_ES = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

export function parseISODate(iso) {
  // Avoid tz drift — interpret YYYY-MM-DD as local midnight
  const [y, m, d] = iso.split("-").map((v) => parseInt(v, 10));
  return new Date(y, m - 1, d);
}

export function toISO(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Single source of truth for "what day is it for the user".
 *
 * Always computed from the browser's *local* clock so that a user in
 * Argentina (UTC-3) at 22:00 of June 10 sees "today = 2026-06-10", even
 * though the server's UTC clock would say it's already June 11. Every API
 * call that touches per-day data must forward this value (`?today=...` or
 * `client_today` in the body).
 */
export function todayLocalISO() {
  return toISO(new Date());
}

export function formatDateLong(iso) {
  const d = parseISODate(iso);
  return `${d.getDate()} de ${MONTHS_FULL[d.getMonth()]}`;
}

export function formatDateShort(iso) {
  const d = parseISODate(iso);
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

export function dayOfWeekShort(iso) {
  return DAYS_ES[parseISODate(iso).getDay()];
}

export function addDays(date, n) {
  const r = new Date(date);
  r.setDate(r.getDate() + n);
  return r;
}

// Returns the Monday (ISO week start) on or before the given date.
export function mondayOf(date) {
  const r = new Date(date);
  const day = r.getDay(); // 0..6, 0=Sun
  const diff = day === 0 ? 6 : day - 1;
  r.setDate(r.getDate() - diff);
  r.setHours(0, 0, 0, 0);
  return r;
}

export function sundayOf(date) {
  const m = mondayOf(date);
  return addDays(m, 6);
}

export function startOfMonth(year, month) {
  return new Date(year, month, 1);
}

export function endOfMonth(year, month) {
  return new Date(year, month + 1, 0);
}

export function monthMatrix(year, month) {
  const first = startOfMonth(year, month);
  const last = endOfMonth(year, month);
  const gridStart = mondayOf(first);
  const gridEnd = sundayOf(last);
  const weeks = [];
  let cursor = new Date(gridStart);
  while (cursor <= gridEnd) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(cursor);
      week.push({
        date: d,
        iso: toISO(d),
        inMonth: d.getMonth() === month,
      });
      cursor = addDays(cursor, 1);
    }
    weeks.push(week);
  }
  return weeks;
}

export function weekDates(focus) {
  const monday = mondayOf(focus);
  return Array.from({ length: 7 }, (_, i) => {
    const d = addDays(monday, i);
    return { date: d, iso: toISO(d) };
  });
}

export const DAYS_ES_FULL = [
  "Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado",
];
export const DAYS_ES_MONDAY_FIRST = ["L", "M", "M", "J", "V", "S", "D"];

export { MONTHS, MONTHS_FULL, DAYS_ES };
